import { useEffect, useState } from 'react'
import { Box, Flex, Grid, Input, Spinner, Text, VStack } from '@chakra-ui/react'
import { LuBuilding2 } from 'react-icons/lu'
import { DataList, DataTable, PageHeader, toaster } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { useAuth } from '../../contexts/AuthContext'
import {
  tenantsService,
  type TenantResponse,
} from '../../api/services/tenants.service'
import { ApiError } from '../../api/client'
import CharCounter from '../../components/CharCounter'
import { TenantFieldLengths } from '../../lib/fieldLengths'

type FormMode = 'create' | 'edit'

interface TenantFormDraft {
  code: string
  name: string
  ruc: string
  legalName: string
  commercialName: string
  prefix: string
  taxAddress: string
  ubigeoCode: string
}

const emptyDraft: TenantFormDraft = {
  code: '',
  name: '',
  ruc: '',
  legalName: '',
  commercialName: '',
  prefix: '',
  taxAddress: '',
  ubigeoCode: '',
}

const Badge = ({ label, bg, color }: { label: string; bg: string; color: string }) => (
  <Box
    display="inline-block"
    px={2} py={0.5}
    borderRadius="md"
    fontSize="10px"
    fontWeight="700"
    textTransform="uppercase"
    letterSpacing="0.05em"
    bg={bg}
    color={color}
  >
    {label}
  </Box>
)

/** Mostrar solo la organización plataforma (Kuntur). Para ver todas, poner false. */
const ONLY_PLATFORM_TENANT = true

export default function TenantsAdminPage() {
  const colors = themeColors
  const { user } = useAuth()
  const allowed = user?.isPlatform ?? false

  const [tenants, setTenants] = useState<TenantResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [search, setSearch] = useState('')

  const [showForm, setShowForm] = useState(false)
  const [formMode, setFormMode] = useState<FormMode>('create')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<TenantFormDraft>(emptyDraft)
  const [submitting, setSubmitting] = useState(false)
  const [hydrating, setHydrating] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const refresh = async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await tenantsService.list()
      // Por ahora solo se opera sobre Kuntur (la organización plataforma): las demás son de
      // prueba y no deben verse. Mismo criterio que el selector oculto del menú de usuario.
      setTenants(ONLY_PLATFORM_TENANT ? list.filter(t => t.isPlatform) : list)
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Error de conexión'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (allowed) refresh()
  }, [allowed])

  if (!allowed) {
    return (
      <Box p={8}>
        <Text color={colors.textMuted}>
          Esta página es solo para usuarios del tenant principal.
        </Text>
      </Box>
    )
  }

  const filtered = tenants.filter(t => {
    const q = search.trim().toLowerCase()
    if (!q) return true
    return t.code.toLowerCase().includes(q) || t.name.toLowerCase().includes(q)
  })

  const openCreate = () => {
    setFormMode('create')
    setEditingId(null)
    setDraft(emptyDraft)
    setFormError(null)
    setShowForm(true)
  }

  const openEdit = async (tenant: TenantResponse) => {
    setFormMode('edit')
    setEditingId(tenant.id)
    setDraft(emptyDraft)
    setFormError(null)
    setShowForm(true)
    setHydrating(true)
    try {
      const detail = await tenantsService.getById(tenant.id)
      setDraft({
        code: detail.code,
        name: detail.name,
        ruc: detail.ruc ?? '',
        legalName: detail.legalName ?? '',
        commercialName: detail.commercialName ?? '',
        prefix: detail.prefix ?? '',
        taxAddress: detail.taxAddress ?? '',
        ubigeoCode: detail.ubigeoCode ?? '',
      })
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : 'No se pudo cargar la organización')
    } finally {
      setHydrating(false)
    }
  }

  const closeForm = () => {
    if (submitting) return
    setShowForm(false)
    setEditingId(null)
    setDraft(emptyDraft)
    setFormError(null)
  }

  const handleSubmit = async () => {
    if (!draft.code.trim() || !draft.name.trim()) {
      setFormError('Código y nombre son obligatorios')
      return
    }
    // El RUC es opcional, pero si se escribe algo tiene que ser válido. Se valida
    // aquí (esta pantalla no usa zod) para no depender del 400 del backend.
    const ruc = draft.ruc.trim()
    if (ruc && !/^\d{11}$/.test(ruc)) {
      setFormError('El RUC debe tener 11 dígitos')
      return
    }
    setSubmitting(true)
    setFormError(null)
    try {
      if (formMode === 'create') {
        await tenantsService.create({
          code: draft.code.trim().toUpperCase(),
          name: draft.name.trim(),
        })
        toaster.success({ description: 'Organización creada' })
      } else if (editingId) {
        await tenantsService.update(editingId, {
          code: draft.code.trim().toUpperCase(),
          name: draft.name.trim(),
          // undefined (no '') al vaciarlo: '' no pasa el formato en el backend.
          ruc: ruc || undefined,
          legalName: draft.legalName.trim() || undefined,
          commercialName: draft.commercialName.trim() || undefined,
          prefix: draft.prefix.trim().toUpperCase() || undefined,
          taxAddress: draft.taxAddress.trim() || undefined,
          ubigeoCode: draft.ubigeoCode.trim() || undefined,
        })
        toaster.success({ description: 'Organización actualizada' })
      }
      closeForm()
      await refresh()
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Error al guardar')
    } finally {
      setSubmitting(false)
    }
  }

  const isCreate = formMode === 'create'
  const submitDisabled = submitting || hydrating

  return (
    <VStack gap={6} alignItems="stretch">
      <PageHeader
        title="Organizaciones"
        subtitle="Empresas que usan el sistema. Crea aquí cada nueva organización; sus usuarios se registran luego desde Personas."
        buttonLabel="Nueva organización"
        onButtonClick={openCreate}
        buttonIcon={<LuBuilding2 size={18} />}
        colors={colors}
      />

      <DataList<TenantResponse>
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por código o nombre..."
        page={1}
        totalPages={1}
        onPageChange={() => {}}
        totalCount={filtered.length}
        entityName="organizaciones"
        brandColor={brand.green}
        headers={['Código', 'Nombre', 'Tipo']}
        data={filtered}
        loading={loading}
        error={error}
        emptyMessage="No hay organizaciones todavía"
        loadingMessage="Cargando organizaciones..."
        colors={colors}
        keyExtractor={(t) => t.id}
        renderRow={(t) => (
          <>
            <DataTable.Cell>
              <Text fontFamily="monospace" fontSize="sm" fontWeight="600" color={colors.text}>{t.code}</Text>
            </DataTable.Cell>
            <DataTable.Cell>
              <Text fontSize="sm" color={colors.text}>{t.name}</Text>
            </DataTable.Cell>
            <DataTable.Cell>
              {t.isPlatform
                ? <Badge label="Plataforma" bg="rgba(212, 154, 42, 0.15)" color="#a8740e" />
                : <Badge label="Cliente" bg="rgba(42, 138, 135, 0.15)" color="#2a8a87" />}
            </DataTable.Cell>
          </>
        )}
        actions={(t) => [
          { label: 'Editar', onClick: () => openEdit(t) },
        ]}
      />

      {showForm && (
        <Box
          position="fixed" top={0} left={0} right={0} bottom={0}
          bg="rgba(0,0,0,0.45)" zIndex={50}
          display="flex" alignItems="center" justifyContent="center"
          onClick={closeForm}
        >
          <Box
            bg={colors.bgCard} border="1px solid" borderColor={colors.border}
            borderRadius="2xl" p={6} maxW={isCreate ? '480px' : '720px'} w="90%"
            maxH="90vh" overflow="auto"
            onClick={(e) => e.stopPropagation()}
          >
            <Text fontSize="lg" fontWeight="700" color={colors.text} mb={1}>
              {isCreate ? 'Nueva organización' : 'Editar organización'}
            </Text>
            <Text fontSize="xs" color={colors.textMuted} mb={4}>
              {isCreate
                ? 'Define el código corto y el nombre comercial. El admin de la organización se crea después desde Personas.'
                : 'Edita la información de identidad. La planta (polígono y componentes) se gestiona desde "Mi Empresa" tras switchear a la organización.'}
            </Text>

            {hydrating ? (
              <Flex justifyContent="center" py={10}>
                <Spinner color={brand.green} />
              </Flex>
            ) : (
              <VStack gap={3} alignItems="stretch" mb={4}>
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                  <Box>
                    <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>
                      Código <Text as="span" color="#c8442f">*</Text>
                    </Text>
                    <Input
                      placeholder="ACME"
                      value={draft.code}
                      onChange={(e) => setDraft(d => ({ ...d, code: e.target.value.toUpperCase().slice(0, TenantFieldLengths.code) }))}
                      maxLength={TenantFieldLengths.code}
                    />
                    <CharCounter value={draft.code} max={TenantFieldLengths.code} />
                  </Box>
                  <Box>
                    <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>
                      Nombre <Text as="span" color="#c8442f">*</Text>
                    </Text>
                    <Input
                      placeholder="Acme Mining S.A.C."
                      value={draft.name}
                      onChange={(e) => setDraft(d => ({ ...d, name: e.target.value.slice(0, TenantFieldLengths.name) }))}
                      maxLength={TenantFieldLengths.name}
                    />
                    <CharCounter value={draft.name} max={TenantFieldLengths.name} />
                  </Box>
                </Grid>

                {!isCreate && (
                  <>
                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                      <Box>
                        <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>RUC</Text>
                        <Input
                          placeholder="20481234567"
                          inputMode="numeric"
                          value={draft.ruc}
                          onChange={(e) => setDraft(d => ({ ...d, ruc: e.target.value.slice(0, TenantFieldLengths.ruc) }))}
                          maxLength={TenantFieldLengths.ruc}
                        />
                        <Flex justifyContent="space-between" alignItems="center" mt={1}>
                          <Text fontSize="xs" color={colors.textMuted}>
                            Identifica a la organización como entidad productora del expediente.
                          </Text>
                          <CharCounter value={draft.ruc} max={TenantFieldLengths.ruc} mt={0} />
                        </Flex>
                      </Box>
                      <Box>
                        <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>Razón social</Text>
                        <Input
                          placeholder="Acme Mining S.A.C."
                          value={draft.legalName}
                          onChange={(e) => setDraft(d => ({ ...d, legalName: e.target.value.slice(0, TenantFieldLengths.legalName) }))}
                          maxLength={TenantFieldLengths.legalName}
                        />
                        <CharCounter value={draft.legalName} max={TenantFieldLengths.legalName} />
                      </Box>
                      <Box>
                        <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>Nombre comercial</Text>
                        <Input
                          placeholder="Acme"
                          value={draft.commercialName}
                          onChange={(e) => setDraft(d => ({ ...d, commercialName: e.target.value.slice(0, TenantFieldLengths.commercialName) }))}
                          maxLength={TenantFieldLengths.commercialName}
                        />
                        <CharCounter value={draft.commercialName} max={TenantFieldLengths.commercialName} />
                      </Box>
                      <Box>
                        <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>Prefijo (siglas)</Text>
                        <Input
                          placeholder="ACM"
                          value={draft.prefix}
                          onChange={(e) => setDraft(d => ({ ...d, prefix: e.target.value.toUpperCase().slice(0, TenantFieldLengths.prefix) }))}
                          maxLength={TenantFieldLengths.prefix}
                        />
                        <Flex justifyContent="space-between" alignItems="center" mt={1}>
                          <Text fontSize="xs" color={colors.textMuted}>
                            Hasta 3 caracteres. Se usa como prefijo en códigos de lote móviles.
                          </Text>
                          <CharCounter value={draft.prefix} max={TenantFieldLengths.prefix} mt={0} />
                        </Flex>
                      </Box>
                      <Box>
                        <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>Ubigeo</Text>
                        <Input
                          placeholder="150101"
                          value={draft.ubigeoCode}
                          onChange={(e) => setDraft(d => ({ ...d, ubigeoCode: e.target.value.slice(0, TenantFieldLengths.ubigeoCode) }))}
                          maxLength={TenantFieldLengths.ubigeoCode}
                        />
                        <CharCounter value={draft.ubigeoCode} max={TenantFieldLengths.ubigeoCode} />
                      </Box>
                    </Grid>
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>Dirección fiscal</Text>
                      <Input
                        placeholder="Av. Lima 123, San Isidro"
                        value={draft.taxAddress}
                        onChange={(e) => setDraft(d => ({ ...d, taxAddress: e.target.value.slice(0, TenantFieldLengths.taxAddress) }))}
                        maxLength={TenantFieldLengths.taxAddress}
                      />
                      <CharCounter value={draft.taxAddress} max={TenantFieldLengths.taxAddress} />
                    </Box>
                  </>
                )}
              </VStack>
            )}

            {formError && (
              <Text color="#c8442f" fontSize="sm" mb={3}>
                {formError}
              </Text>
            )}

            <Flex justifyContent="flex-end" gap={2}>
              <Box
                as="button"
                onClick={closeForm}
                disabled={submitting}
                px={4} h="40px"
                borderRadius="lg"
                fontSize="sm" fontWeight="600"
                color={colors.textSecondary}
                bg="transparent"
                border="1px solid"
                borderColor={colors.border}
                cursor={submitting ? 'not-allowed' : 'pointer'}
                opacity={submitting ? 0.6 : 1}
              >
                Cancelar
              </Box>
              <Box
                as="button"
                onClick={handleSubmit}
                disabled={submitDisabled}
                px={5} h="40px"
                borderRadius="lg"
                fontSize="sm" fontWeight="700"
                color="white"
                bg={brand.green}
                cursor={submitDisabled ? 'not-allowed' : 'pointer'}
                opacity={submitDisabled ? 0.7 : 1}
                display="flex"
                alignItems="center"
                gap={2}
              >
                {submitting && <Spinner size="sm" />}
                {isCreate ? 'Crear organización' : 'Guardar cambios'}
              </Box>
            </Flex>
          </Box>
        </Box>
      )}
    </VStack>
  )
}
